import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';

import SubCard from 'ui-component/cards/SubCard';
import CheckoutBackdrop from 'ui-component/loader/CheckoutBackdrop';
import { getAddonsUrl, purchasChargeAddonUrl } from 'api/hiredrateURLConstruct';
import { useNotification } from 'store/context/Notification';
import { ERROR } from 'store/constant';
import HDConfirmationDialog from 'ui-component/dialog/HDConfirmationDialog';
import AppConfig from 'api/AppConfig';
// import HDConfirmationDialog from 'ui-component/confirm-dialog/HDConfirmationDialog';

const AddonListing = () => {
  const theme = useTheme();
  const { companyID } = useSelector((state) => state.account);
  const { showNotification } = useNotification();

  const [addons, setAddons] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState("");
  const [processingChargeId, setProcessingChargeId] = useState(null);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const configObject = AppConfig("APIConfig");

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAddon, setPendingAddon] = useState(null);

  useEffect(() => {
    fetchAddons(1, true);
  }, []);

  const fetchAddons = async (pageNo = 1, replace = false) => {
    try {
      if (pageNo === 1) {
        setInitialLoading(true);
      }

      const response = await axios.get(
        getAddonsUrl(pageNo, 20),
        configObject
      );

      const responseData = response.data;

      setHasNext(responseData?.has_next || false);
      setPage(pageNo);

      if (replace) {
        setAddons(responseData?.data || []);
      } else {
        setAddons((prev) => [...prev, ...(responseData?.data || [])]);
      }
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.message ||
        'Something went wrong';

      showNotification(ERROR, message, ERROR, "Subscription");
    } finally {
      setInitialLoading(false);
    }
  };

  // Show confirmation dialog when user clicks "Purchase Now"
  const handlePurchaseClick = (addon) => {
    if (!addon || !companyID) {
      return;
    }
    setPendingAddon(addon);
    setShowConfirmDialog(true);
  };

  // Handle confirmation and redirect
  const handleConfirmPurchase = async () => {
    if (!pendingAddon || !companyID) {
      return;
    }

    setShowConfirmDialog(false);
    setProcessingChargeId(pendingAddon.addon_unique_id);
    setCheckoutLoading(true);
    setSelectedAddon(pendingAddon.name);

    try {
      const response = await axios.post(
        purchasChargeAddonUrl(),
        {
          company_id: parseInt(companyID),
          charge_id: pendingAddon.addon_unique_id,
          quantity: 1,
          redirect_url:
            window.location.origin +
            '/settings/addons/payment-success'
        },
        configObject,
      );

      if (
        response?.data?.status === 'success' &&
        response?.data?.checkout_url
      ) {
        window.location.href = response.data.checkout_url;
      } else {
        setCheckoutLoading(false);
      }
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.message ||
        'Something went wrong';

      console.error(err);
      showNotification(ERROR, message, ERROR, "Subscription");
      setCheckoutLoading(false);
    } finally {
      setProcessingChargeId(null);
      setPendingAddon(null);
    }
  };

  // Handle cancel
  const handleCancelPurchase = () => {
    setShowConfirmDialog(false);
    setPendingAddon(null);
  };

  const getCurrencySymbol = (currencyId) => {
    const currencies = {
      20: '₹',
      1: '$',
      2: '€',
      3: '£'
    };

    return currencies[currencyId] || '$';
  };

  const getBadgeColor = (chargeType) => {
    if (chargeType === 'recurring') {
      return {
        bg: theme.palette.secondary.light,
        text: theme.palette.secondary.dark,
        border: theme.palette.secondary.main
      };
    }

    return {
      bg: theme.palette.primary.light,
      text: theme.palette.primary.dark,
      border: theme.palette.primary.main
    };
  };

  // Get confirmation message
  const getConfirmationMessage = () => {
    if (!pendingAddon) return "";
    return `You are about to purchase the ${pendingAddon.name} addon for ${getCurrencySymbol(pendingAddon.currency_id)}${pendingAddon.price} ${pendingAddon.charge_type === 'recurring' ? 'per month' : 'one-time'}. You will be redirected to Chargebee to complete the payment.`;
  };

  return (
    <SubCard
      title="Purchase Addons"
      sx={{
        background: theme.palette.background.paper,
        borderRadius: '18px'
      }}
    >
      {/* Confirmation Dialog */}
      <HDConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelPurchase}
        onConfirm={handleConfirmPurchase}
        title="Confirm Addon Purchase"
        message={getConfirmationMessage()}
        confirmButtonText="Proceed to Payment"
        cancelButtonText="Cancel"
        loading={checkoutLoading}
      >
        {pendingAddon && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: theme.palette.grey[50],
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Addon Details:
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Name: {pendingAddon.name}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary" sx={{ mt: 0.5 }}>
                Price: {getCurrencySymbol(pendingAddon.currency_id)}{pendingAddon.price}
                {pendingAddon.charge_type === 'recurring' ? ' / month' : ' / once'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Type: {pendingAddon.charge_type === 'recurring' ? 'Recurring' : 'One Time'}
              </Typography>
              
              {/* Show limits if available */}
              {pendingAddon.limits && pendingAddon.limits.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Includes:
                  </Typography>
                  {pendingAddon.limits.slice(0, 2).map((limit, index) => (
                    <Typography key={index} variant="body2" sx={{ fontSize: '0.85rem' }}>
                      • {limit.limiter_name}: {limit.no_of_limit} {limit.operation_name}
                    </Typography>
                  ))}
                  {pendingAddon.limits.length > 2 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      + {pendingAddon.limits.length - 2} more features
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              bgcolor: theme.palette.warning.light + '20',
              borderRadius: 1,
              border: `1px solid ${theme.palette.warning.light}`
            }}>
              <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                 You will be redirected to Chargebee to complete the payment.
              </Typography>
            </Box>
          </Box>
        )}
      </HDConfirmationDialog>

      {/* Checkout Backdrop */}
      <CheckoutBackdrop
        open={checkoutLoading}
        title="Redirecting to payment page..."
        description="Please wait while we prepare your secure checkout session."
        selectedItem={selectedAddon}
        selectedItemLabel="Selected Add-on"
        redirectText="Redirecting to Chargebee..."
        providerName="Chargebee"
      />
      
      {initialLoading ? (
        <Box
          sx={{
            minHeight: 420,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress color="primary" />
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Loading addons...
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(320px, 360px))',
              justifyContent: 'flex-start',
              gap: 3,
              py: 1
            }}
          >
            {addons.map((addon) => {
              const isProcessing =
                processingChargeId === addon.addon_unique_id;

              const badge = getBadgeColor(addon.charge_type);

              return (
                <Box
                  key={addon.addon_id}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 470,
                    borderRadius: '22px',
                    overflow: 'hidden',
                    background:
                      theme.palette.background.paper,
                    border: `1px solid ${theme.palette.grey[100]}`,
                    boxShadow:
                      '0 6px 24px rgba(0,0,0,0.06)',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {/* HEADER */}
                  <Box
                    sx={{
                      position: 'relative',
                      background: `
                        linear-gradient(
                          135deg,
                          ${theme.palette.primary.main} 0%,
                          ${theme.palette.primary.dark} 55%,
                          ${theme.palette.secondary.main} 100%
                        )
                      `,
                      px: 3,
                      pt: 2.5,
                      pb: 3.5
                    }}
                  >
                    {/* decorative circles */}
                    <Box
                      sx={{
                        position: 'absolute',
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        background:
                          'rgba(255,255,255,0.08)',
                        top: -70,
                        right: -50
                      }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        position: 'relative',
                        zIndex: 2
                      }}
                    >
                      <Chip
                        label={
                          addon.charge_type === 'recurring'
                            ? 'Recurring'
                            : 'One Time'
                        }
                        size="small"
                        sx={{
                          background: badge.bg,
                          color: badge.text,
                          fontWeight: 700,
                          border: `1px solid ${badge.border}`
                        }}
                      />
                    </Box>

                    <Typography
                      variant="h3"
                      sx={{
                        mt: 3,
                        mb: 1.5,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        position: 'relative',
                        zIndex: 2
                      }}
                    >
                      {addon.name}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 0.5,
                        position: 'relative',
                        zIndex: 2
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '2.8rem',
                          lineHeight: 1
                        }}
                      >
                        {getCurrencySymbol(addon.currency_id)}
                        {addon.price}
                      </Typography>

                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.8)',
                          mb: 0.5,
                          fontWeight: 500
                        }}
                      >
                        {addon.charge_type === 'recurring'
                          ? '/month'
                          : '/once'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* BODY */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      flex: 1,
                      px: 3,
                      py: 3
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color:
                            theme.palette.text.secondary,
                          letterSpacing: '0.08em',
                          mb: 2
                        }}
                      >
                        WHAT'S INCLUDED
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5
                        }}
                      >
                        {(addon.limits || []).map(
                          (limit, index) => (
                            <Box
                              key={index}
                              sx={{
                                background:
                                  theme.palette.grey[50],
                                border: `1px solid ${theme.palette.grey[100]}`,
                                borderRadius: '16px',
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                justifyContent:
                                  'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: '0.92rem',
                                    fontWeight: 700,
                                    color:
                                      theme.palette.text
                                        .primary
                                  }}
                                >
                                  {limit.limiter_name}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: '0.72rem',
                                    color:
                                      theme.palette.text
                                        .secondary,
                                    textTransform:
                                      'uppercase'
                                  }}
                                >
                                  {limit.operation_name}
                                </Typography>
                              </Box>

                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  fontSize: '1.4rem',
                                  color:
                                    theme.palette.primary
                                      .main
                                }}
                              >
                                {limit.no_of_limit}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>

                    {/* FOOTER */}
                    <Box sx={{ mt: 3 }}>
                      {addon.validity && (
                        <Box
                          sx={{
                            mb: 2,
                            background:
                              theme.palette.warning.light,
                            borderRadius: '14px',
                            px: 2,
                            py: 1.5,
                            display: 'flex',
                            justifyContent:
                              'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.82rem',
                              fontWeight: 700,
                              color: theme.palette.warning.dark
                            }}
                          >
                            ⏱ Valid for plan duration
                          </Typography>
                        </Box>
                      )}

                      <Button
                        fullWidth
                        variant="contained"
                        disabled={checkoutLoading}
                        onClick={() => handlePurchaseClick(addon)}
                        sx={{
                          height: 52,
                          borderRadius: '14px',
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          boxShadow: 'none',
                          background: `
                            linear-gradient(
                              135deg,
                              ${theme.palette.primary.main},
                              ${theme.palette.primary.dark}
                            )
                          `,
                          '&:hover': {
                            boxShadow: '0 10px 24px rgba(0,0,0,0.16)'
                          }
                        }}
                      >
                        Purchase Now
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {hasNext && (
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Button
                onClick={() =>
                  fetchAddons(page + 1)
                }
                variant="outlined"
                sx={{
                  borderRadius: '14px',
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 700
                }}
              >
                Load More
              </Button>
            </Box>
          )}
        </>
      )}
    </SubCard>
  );
};

export default AddonListing;